// utils
import { pushDataLayer } from "@tajawal/web-core-sdk-v2/lib/utils/gtm.utils";
import {
  getCommonAVOParams,
  getTotalEventPrice,
  isDateChanged,
  isSeatsRemainingThreshold,
  isSSR,
} from "@/utils/compute.utils";
import _find from "lodash/find";
import _get from "lodash/get";
import _size from "lodash/size";
import _map from "lodash/map";
import _split from "lodash/split";
import _join from "lodash/join";
import _uniq from "lodash/uniq";
import _some from "lodash/some";
import _toString from "lodash/toString";
import _min from "lodash/min";
import _filter from "lodash/filter";
import _capitalize from "lodash/capitalize";

// constants
import {
  AVO_EVENTS,
  AVO_EVENT_CATEGORIES,
  AVO_EVENT_NAMES,
  EVENTS,
} from "@/constants/analytics.constants";
import { APPLE_PROVIDER, PLATFORM, SAR } from "@/constants/common.constants";

// types
import {
  CountryCode,
  Language,
} from "@tajawal/web-core-sdk-v2/lib/types/common.types";
import { Platform } from "@/types/common.types";
import { SearchBoxState } from "@tajawal/web-flights-sdk/lib/types/search-box.types";
import {
  DATE_FORMAT_MAP,
  DATE_WITH_TIME_FORMAT,
  TIME_FORMAT_24,
} from "@/constants/date.constants";
import {
  DOMESTIC_ID_SEPARATOR,
  INBOUND,
  OUTBOUND,
  SEARCH_BOX_CONSTANTS,
  TRAVELLERS_TYPES_CODES_MAP,
} from "@tajawal/web-flights-sdk/lib/constants";
import { formatDate } from "./date.utils";
import { ParsedSearchQuery } from "@tajawal/web-flights-sdk/lib/types/search-request.types";
import {
  DomesticItinerary,
  ItineraryModel,
  ItineraryPrice,
} from "@tajawal/web-flights-sdk/lib/types/itinerary.types";
import { LegModel } from "@tajawal/web-flights-sdk/lib/types/leg.types";
import type FlightInfoStore from "@tajawal/web-flights-sdk/lib/stores/flight-info.store";
import format from "date-fns/format";
import {
  FareRuleModel,
  FareRulesModel,
} from "@tajawal/web-flights-sdk/lib/types/fare-rules.types";
import { APP_PAGES, LANGUAGE } from "@tajawal/web-core-sdk-v2/lib/constants";
import { FlightProductModel } from "@tajawal/web-flights-sdk/lib/types/flight-product.types";
import {
  asUTC,
  type CurrencyStore,
  mapInitialStateToStoreState,
} from "@tajawal/web-core-sdk-v2";
import { AddOnsModel } from "@tajawal/web-flights-sdk/lib/types/add-ons.types";
import { LegBaggageInfo } from "@tajawal/web-flights-sdk/lib/types/baggage.types";
import { getParsedSearchQuery } from "@tajawal/web-flights-sdk/lib/utils/search.utils";
import { BrandedFaresModel } from "@tajawal/web-flights-sdk/lib/types/fare-families.types";
import {
  FlightsAddonAddedEventParams,
  FlightsAddonBenefitsViewedEventParams,
  FlightsAddonPageLoadedEventParams,
  FlightsAddonRecaptureAddedEventParams,
  FlightsAddonRecaptureSkippedEventParams,
  FlightsAddonRecaptureViewedEventParams,
  FlightsAddonRemovedEventParams,
  FlightsAddonSkippedEventParams,
  FlightsDpBaggageEventParams,
  FlightsDpFareRulesEventParams,
  FlightsDpPageLoadedEventParams,
  FlightsFareFamiliesErrorEventParams,
  FlightsFareFamiliesReloadedEventParams,
  FlightsFareFamiliesScrollEventParams,
  FlightsFareFamiliesSelectedEventParams,
  FlightsFareFamiliesUpgradedEventParams,
  FlightsFlightConditionsAcceptedEventParams,
  FlightsFlightConditionsEventParams,
  FlightsFlightConditionsViewedEventParams,
  FlightsGpExtraBaggageAddedEventParams,
  FlightsGpFinalDetailsClickedEventParams,
  FlightsGpFrequentFlyerAddedEventParams,
  FlightsGpPageLoadedEventParams,
  FlightsSpCabinClassSelectedEventParams,
  FlightsSpDestinationFieldEnteredEventParams,
  FlightsSpOriginFieldEnteredEventParams,
  FlightsSpPageLoadedEventParams,
  FlightsSpPassengersSelectedEventParams,
  FlightsSpRecentSearchCardClearedEventParams,
  FlightsSpReturnTravelDateSelectedEventParams,
  FlightsSpStartedEventParams,
  FlightsSpTravelDateSelectedEventParams,
  FlightsSrpAdAvailableEventParams,
  FlightsSrpAdClickedEventParams,
  FlightsSrpAdDisplayedEventParams,
  FlightsSrpEasyFiltersEventParams,
  FlightsSrpFareCalendarEventParams,
  FlightsSrpFareFamiliesUpgradedEventParams,
  FlightsSrpFiltersEventParams,
  FlightsSrpFlightCardDetailsClickedEventParams,
  FlightsSrpFlightCardViewedEventParams,
  FlightsSrpFlightDetailsViewedEventParams,
  FlightsSrpInboundFlightCardViewedEventParams,
  FlightsSrpInboundFlightSelectedEventParams,
  FlightsSrpModifySearchCancelledEventParams,
  FlightsSrpModifySearchClickedEventParams,
  FlightsSrpNearbyAirportsClickEventParams,
  FlightsSrpNearbyAirportsViewEventParams,
  FlightsSrpOutboundFareFamilySelectedEventParams,
  FlightsSrpOutboundFlightCardViewedEventParams,
  FlightsSrpOutboundFlightSelectedEventParams,
  FlightsSrpPageLoadedEventParams,
  FlightsSrpQuickFiltersEventParams,
  FlightsSrpSameAirlineFilterEventParams,
  FlightsSrpSortByOpenedEventParams,
  FlightsSrpSortBySelectedEventParams,
  FlightsSrpSortEventParams,
  MyAccountLoginCtaClickedEventParams,
  PaymentPdpPageLoadedEventParams,
} from "@/types/avo.types";
import { SSRModel } from "@tajawal/web-flights-sdk/lib/types/ssr.types";
import { sha256 } from "@tajawal/web-common/lib";
import { RuleProduct } from "@tajawal/web-flights-sdk/lib/types/cart.types";
import { AdPlacement } from "@tajawal/web-core-sdk-v2/lib/types/ad-solution.types";
import { TravellerModel } from "@tajawal/web-flights-sdk/lib/types/traveller.types";
import { useQuery } from "@/hooks/useQuery";

let avoParams: AvoParams;
const eventsQueue: any[] = [];

type AvoInitialState = {
  countryCode: CountryCode;
  isMobile: boolean;
  hasVerifiedPhone: boolean;
  hasVerifiedEmail: boolean;
  isLoggedIn: boolean;
  userId: string;
  currency: string;
  language: Language;
  splitKey: string;
};

type EventCommonData = {
  platform?: Platform;
  pos?: CountryCode;
  site_currency?: string;
  email_verified?: boolean;
  is_user_logged_in?: boolean;
  site_language?: Language;
  phone_verified?: boolean;
  split_key?: string;
  user_id?: string;
};

type EventData<T> = {
  event: string;
  event_category: string;
  event_name: string;
} & Omit<T, keyof EventCommonData>;

export class AvoParams {
  pos: CountryCode;
  platform: Platform;
  hasVerifiedPhone: boolean;
  hasVerifiedEmail: boolean;
  isLoggedIn: boolean;
  userId: string;
  currency: string;
  language: Language;
  splitKey: string;

  constructor(initialState: AvoInitialState) {
    this.updateInitialState(initialState);
    avoParams = this;
    if (_size(eventsQueue) > 0) {
      pushDataLayerWithParams(eventsQueue.pop());
    }
  }

  updateInitialState(initialState: AvoInitialState): void {
    mapInitialStateToStoreState(this, initialState);
    this.pos = initialState.countryCode;
    this.platform = initialState.isMobile ? PLATFORM.MOBILE : PLATFORM.DESKTOP;
  }
}

const FLIGHT_DETAILS_PAGE_EVENT_MAP = {
  [APP_PAGES.FLIGHTS_RESULTS]: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
  [APP_PAGES.FLIGHTS_PAX]: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
  [APP_PAGES.FLIGHTS_EXTRAS]: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
};

export function isAvoInitialized(): boolean {
  return !!avoParams;
}

export function initAvoParams(
  initialData: AvoInitialState,
  reInitialize = false,
): AvoParams {
  // Always make a new instance if server, otherwise state is shared between requests
  if (isSSR() || reInitialize || !avoParams) {
    avoParams = new AvoParams(initialData);
  }

  return avoParams;
}

export function pushDataLayerWithParams<T>(data: EventData<T>) {
  if (!avoParams) {
    eventsQueue.push(data);
    return;
  }

  if (_size(eventsQueue) > 0) {
    pushDataLayerWithParams(eventsQueue.pop());
  }

  return pushDataLayer({
    ...data,
    platform: avoParams?.platform,
    pos: avoParams?.pos,
    site_currency: avoParams?.currency,
    email_verified: !!avoParams?.hasVerifiedEmail,
    is_user_logged_in: !!avoParams?.isLoggedIn,
    site_language: avoParams?.language,
    phone_verified: !!avoParams?.hasVerifiedPhone,
    split_key: avoParams?.splitKey,
    ...(!!avoParams?.userId && {
      user_id: avoParams?.userId,
    }),
  });
}

// TODO: @ali fix this and other event types later on that have partial
export function pushCabinSelectedGtmEvent(cabin: string) {
  return pushDataLayerWithParams<
    Partial<FlightsSpCabinClassSelectedEventParams>
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: "flights_sp_cabinClass_selected",
    cabin_class: cabin,
  });
}

export function pushAirportSelectionGtmEvent(
  airports: string[],
  cityName: string,
  isOrigin: boolean,
) {
  const action = isOrigin
    ? "flights_sp_originField_entered"
    : "flights_sp_destinationField_entered";
  const keyPrefix = isOrigin ? "origin" : "destination";

  return pushDataLayerWithParams<
    Partial<
      | FlightsSpOriginFieldEnteredEventParams
      | FlightsSpDestinationFieldEnteredEventParams
    >
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: action,
    [`${keyPrefix}_airport_codes`]: JSON.stringify(airports),
    [`${keyPrefix}_cityName_en`]: cityName,
  });
}

export function pushSearchBoxLoadedGtmEvent(
  state: SearchBoxState,
  isRoundTrip: boolean,
) {
  const {
    adults,
    children,
    infants,
    legs,
    searchType,
    showFareCalenderCheckBox,
    hasBaggageOnly,
    directFlightsOnly,
  } = state;

  const { departureDate, returnDate, destinationIata, originIata } =
    legs[0] || {};

  return pushDataLayerWithParams<FlightsSpPageLoadedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: "flights_sp_pageLoaded",
    origin_iata: originIata,
    destination_iata: destinationIata,
    adult_pax: adults,
    infant_pax: infants,
    trip_type: searchType,
    checked_baggage: hasBaggageOnly,
    show_price_calendar: showFareCalenderCheckBox,
    direct_flight: directFlightsOnly,
    child_pax: children,
    departure_date: departureDate,
    ...(!!isRoundTrip && { return_date: returnDate }),
  });
}

export function pushPassengersChangeGtmEvent({
  adults,
  children,
  infants,
}: {
  adults: number;
  children: number;
  infants: number;
}) {
  return pushDataLayerWithParams<
    Partial<FlightsSpPassengersSelectedEventParams>
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: "flights_sp_passengers_selected",
    adult_pax: adults,
    infant_pax: infants,
    child_guests: children,
  });
}

export function pushClearRecentSearchGtmEvent() {
  return pushDataLayerWithParams<FlightsSpRecentSearchCardClearedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: "flights_sp_recentSearchCard_cleared",
    card_position: 0,
  });
}

export function pushSelectTravelDateGtmEvent(
  date: Date,
  isDeparture: boolean,
  query: string,
  dateColor: string,
  datePrice: number,
) {
  if (!isDeparture) {
    return pushReturnSelectTravelDateGtmEvent(date, query);
  }
  const { originId, destinationId, isDomesticKSA, tripType } =
    getCommonAVOParams(query);
  const action = "flights_sp_travelDate_selected";
  const formatStr = DATE_FORMAT_MAP[SEARCH_BOX_CONSTANTS.REQUEST_DATE_FORMAT];

  const formatedDepartureDate = formatDate(date, formatStr);
  const bookingWindow = Math.ceil(
    (date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
  );
  return pushDataLayerWithParams<FlightsSpTravelDateSelectedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: action,
    travel_date: formatedDepartureDate,
    price_select_colour: dateColor ? _capitalize(dateColor) : null,
    price_available: datePrice ? datePrice > 0 : null,
    booking_window: bookingWindow,
    origin_iata: originId,
    destination_iata: destinationId,
    trip_type: tripType,
    flight_type: isDomesticKSA ? "dom" : "int",
  });
}

export function pushReturnSelectTravelDateGtmEvent(date: Date, query: string) {
  const { originId, destinationId, isDomesticKSA, tripType } =
    getCommonAVOParams(query);
  const action = "flights_sp_returnTravelDate_selected";
  const formatStr = DATE_FORMAT_MAP[SEARCH_BOX_CONSTANTS.REQUEST_DATE_FORMAT];

  const formatedReturnDate = formatDate(date, formatStr);
  return pushDataLayerWithParams<FlightsSpReturnTravelDateSelectedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: action,
    return_date: formatedReturnDate,
    price_select_colour: null,
    price_available: null,
    origin_iata: originId,
    destination_iata: destinationId,
    trip_type: tripType,
    flight_type: isDomesticKSA ? "dom" : "int",
  });
}

export function pushSearchInitiatedGtmEvent(state: SearchBoxState) {
  const {
    adults,
    children,
    infants,
    showFareCalenderCheckBox,
    hasBaggageOnly,
    directFlightsOnly,
    cabin,
  } = state;

  return pushDataLayerWithParams<Partial<FlightsSpStartedEventParams>>({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: "flights_sp_started",
    adult_pax: adults,
    infant_pax: infants,
    checked_baggage: hasBaggageOnly,
    show_price_calendar: showFareCalenderCheckBox,
    direct_flight: directFlightsOnly,
    child_pax: children,
    cabin_class: cabin,
  });
}

export function pushRecentSearchDataGtmEvent(state: ParsedSearchQuery) {
  const { adult, child, infant, cabin } = state;

  return pushDataLayerWithParams<Partial<FlightsSpStartedEventParams>>({
    event: AVO_EVENTS.FLIGHT_SEARCH,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH,
    event_name: "flights_sp_started",
    adult_pax: adult,
    child_pax: child,
    infant_pax: infant,
    show_price_calendar: false,
    checked_baggage: false,
    direct_flight: false,
    cabin_class: cabin,
  });
}

export function pushCalendarPriceAccuracy({
  calendarPrice,
  searchPrice,
  searchRequest,
  isDirect,
  calendarAirline,
  searchAirline,
  airlineFilters,
  source,
  dateClicked = false,
  clickedDates = {
    departureDate: null,
    returnDate: null,
  },
}: {
  calendarPrice: number;
  searchPrice: number;
  searchRequest: ReturnType<typeof useQuery>;
  calendarAirline: string;
  searchAirline: string;
  airlineFilters: string;
  isDirect: boolean;
  source: string;
  dateClicked: boolean;
  clickedDates?: {
    departureDate?: string;
    returnDate?: string;
  };
}) {
  let priceMatch = false;
  const {
    originId,
    destinationId,
    cabinType,
    tripType,
    parsedSearchQuery,
    adultCount,
    childCount,
    infantCount,
    totalPax,
    isDomesticKSA,
  } = searchRequest || {};
  const priceDiff =
    calendarPrice && searchPrice ? searchPrice - calendarPrice : null;
  const percentageDifference =
    priceDiff !== null
      ? Math.abs(((searchPrice - calendarPrice) / calendarPrice) * 100)
      : null;
  if (
    calendarPrice &&
    searchPrice &&
    Number(searchPrice.toFixed(1)) === Number(calendarPrice.toFixed(1))
  ) {
    priceMatch = true;
  }

  return pushDataLayerWithParams<any>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_SRP_FARE_CALENDAR_ACCURACY,
    calendar_price: calendarPrice || null,
    search_price: searchPrice,
    calendar_airline: calendarAirline,
    search_airline: searchAirline,
    airline_filter_applied: airlineFilters || null,
    percentage_difference: percentageDifference,
    price_difference: priceDiff,
    price_match: priceMatch,
    origin_iata: originId,
    destination_iata: destinationId,
    cabin_class: cabinType,
    flight_type: isDomesticKSA ? "dom" : "int",
    trip_type: tripType,
    return_date: parsedSearchQuery?.returnDate || null,
    departure_date: parsedSearchQuery?.departureDate,
    adult_pax: adultCount,
    total_pax: totalPax,
    infant_pax: childCount,
    child_pax: infantCount,
    direct_flight: isDirect,
    served_from: source,
    date_clicked: dateClicked,
    route: `${originId}-${destinationId}`,
    ...(clickedDates?.departureDate && {
      departure_date: clickedDates.departureDate,
    }),
    ...(clickedDates?.returnDate && {
      return_date: clickedDates.returnDate,
    }),
  });
}

export function pushFareCalendarGtmEvent({
  searchRequest,
  calendarClick,
}: {
  searchRequest: ReturnType<typeof useQuery>;
  calendarClick: "expanded" | "collapsed";
}) {
  const {
    originId,
    destinationId,
    cabinType,
    tripType,
    adultCount,
    childCount,
    infantCount,
    totalPax,
    isDomesticKSA,
  } = searchRequest || {};

  // @ts-ignore the event params are defined in avo but not pulled correctly
  return pushDataLayerWithParams<FlightsSrpFareCalendarEventParams | any>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_FARE_CALENDAR,
    calendar_click: calendarClick,
    origin_iata: originId,
    destination_iata: destinationId,
    cabin_class: cabinType,
    trip_type: tripType,
    flight_type: isDomesticKSA ? "dom" : "int",
    total_pax: totalPax,
    adult_pax: adultCount,
    child_pax: childCount,
    infant_pax: infantCount,
    route: `${originId}-${destinationId}`,
    ...(!!avoParams?.userId && {
      user_id: avoParams?.userId,
    }),
  });
}

export function pushFilterChangeGtmEvent(
  name: string,
  value,
  isQuickFilter = false,
) {
  pushDataLayerWithParams<FlightsSrpFiltersEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_FILTERS,
    filter_name: name,
    filter_values: value,
    popular_filter: isQuickFilter,
  });

  isQuickFilter && pushQuickFilterEvent(name, value);
}

export function pushSortByOpenedEvent() {
  return pushDataLayerWithParams<FlightsSrpSortByOpenedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_SORT_BY_OPENED,
  });
}

export function pushSortByEvent(eventName, sortBy) {
  pushDataLayerWithParams<
    FlightsSrpSortEventParams | FlightsSrpSortBySelectedEventParams
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: eventName,
    sort_by: sortBy,
  });
}

export function pushSortBySelectedEvent(item: string) {
  pushSortByEvent(AVO_EVENT_NAMES.FLIGHTS_RSP_SORT, item);
  pushSortByEvent(AVO_EVENT_NAMES.FLIGHTS_RSP_SORT_BY_SELECTED, item);
}

export function pushModifySearchClickedEvent({
  currentQuery,
  newQuery,
  datePrice,
  dateColor,
}: {
  currentQuery: string;
  newQuery: string;
  datePrice: number;
  dateColor: string;
}) {
  const {
    originId,
    destinationId,
    cabinType,
    isDomesticKSA,
    tripType,
    totalPax,
    adult,
    child,
    infant,
  } = getCommonAVOParams(newQuery);
  const dateChanged = isDateChanged(currentQuery, newQuery);
  return pushDataLayerWithParams<FlightsSrpModifySearchClickedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_MODIFY_SEARCH_CLICKED,
    origin_iata: originId,
    destination_iata: destinationId,
    cabin_class: cabinType,
    flight_type: isDomesticKSA ? "dom" : "int",
    trip_type: tripType,
    adult_pax: adult,
    total_pax: totalPax,
    infant_pax: infant,
    child_pax: child,
    date_changed: dateChanged,
    price_available: dateChanged ? (datePrice ? true : false) : null,
    price_select_colour: dateChanged
      ? dateColor
        ? _capitalize(dateColor)
        : "Null"
      : null,
  });
}

export function pushSeoGtmEvent(seoEvent: any) {
  return pushDataLayerWithParams(seoEvent);
}

export function pushModifySearchCancelledEvent() {
  return pushDataLayerWithParams<FlightsSrpModifySearchCancelledEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_MOIDFY_SEARCH_CANCELLED,
  });
}

export function pushFlightSelectedEvent({
  itinerary,
  $resources,
  isFareFamilyUpgraded,
  timeOnPage,
  hasGroup,
}: {
  itinerary: ItineraryModel | DomesticItinerary;
  $resources: FlightInfoStore["$resources"];
  isFareFamilyUpgraded: boolean;
  timeOnPage: number;
  hasGroup: boolean;
}) {
  const {
    searchRequest,
    id,
    isMultiPNR,
    uniqueCarriersArr,
    outboundSeatsAvailable,
    inboundSeatsAvailable,
  } = (itinerary || {}) as DomesticItinerary;
  const seatsAvailable = (itinerary as ItineraryModel)?.seatsAvailable;
  const {
    isRoundTrip,
    originId,
    preferredCabin,
    destinationId,
    searchType,
    totalPax,
    pax,
    isDomesticKSA,
  } = searchRequest || {};

  const airlineName = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");

  const seatsRemaining = isRoundTrip
    ? _min([outboundSeatsAvailable, inboundSeatsAvailable])
    : seatsAvailable;
  const seatsToSend = isSeatsRemainingThreshold(seatsRemaining, false)
    ? seatsRemaining.toString()
    : "unavailable";

  return pushDataLayerWithParams<any>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_CARD_SELECTED,
    search_id: _join(_split(id, DOMESTIC_ID_SEPARATOR), ","),
    price: getTotalEventPrice(itinerary, isRoundTrip as boolean),
    is_multi_pnr: isMultiPNR,
    airline_name_en: airlineName,
    fare_family_upgraded: isFareFamilyUpgraded,
    time_on_page: timeOnPage,
    seats_remaining: String(seatsToSend),
    grouped_flight: hasGroup,
    origin_iata: originId,
    destination_iata: destinationId,
    cabin_class: preferredCabin,
    flight_type: isDomesticKSA ? "dom" : "int",
    trip_type: searchType,
    adult_pax: typeof pax === "object" && "adult" in pax ? pax.adult : 0,
    total_pax: Number(totalPax),
    infant_pax: typeof pax === "object" && "infant" in pax ? pax.infant : 0,
    child_pax: typeof pax === "object" && "child" in pax ? pax.child : 0,
  });
}

export function pushFlightDetailsViewedEvent({
  itinerary,
  page,
}: {
  itinerary: ItineraryModel | DomesticItinerary;
  page: string;
}) {
  const { id } = itinerary;

  return pushDataLayerWithParams<FlightsSrpFlightDetailsViewedEventParams>({
    event:
      FLIGHT_DETAILS_PAGE_EVENT_MAP[page] || AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_CARD_DETAILS_VIEWED,
    search_id: id,
  });
}

export function pushFlightCardDetailsClickedEvent({
  cardPosition,
  searchId,
}: {
  cardPosition: number;
  searchId: string;
}) {
  return pushDataLayerWithParams<FlightsSrpFlightCardDetailsClickedEventParams>(
    {
      event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
      event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
      event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_CARD_DETAILS_CLICKED,
      card_position: cardPosition,
      search_id: searchId,
    },
  );
}

export function pushFareFamilyUpgradedEvent({
  price,
  hasCheckinBaggage,
  isCancellationAvailable,
  cancellationFee,
  dateChangeAvailable,
  dateChangeFee,
  cardPosition,
  numberOfFareFamilies,
  itinerary,
}: {
  price: number;
  hasCheckinBaggage: boolean;
  isCancellationAvailable: boolean;
  cancellationFee: number;
  dateChangeAvailable: boolean;
  dateChangeFee: number;
  cardPosition: number;
  numberOfFareFamilies: number;
  itinerary: ItineraryModel;
}) {
  const canAddExtraBaggage =
    !!itinerary &&
    _some(
      itinerary?.baggageInfo,
      (legBaggageInfo: LegBaggageInfo) =>
        !!legBaggageInfo?.freeBaggage?.baggageOptionsAvailable,
    );
  return pushDataLayerWithParams<FlightsSrpFareFamiliesUpgradedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_FARE_FAMILIES_UPGRADED,
    price,
    checkin_baggage: hasCheckinBaggage,
    cancellation_availability: isCancellationAvailable,
    cancellation_fee: String(cancellationFee),
    datechange_availability: dateChangeAvailable,
    date_change_fee: String(dateChangeFee),
    card_position: cardPosition,
    number_of_cards: numberOfFareFamilies,
    extra_baggage: canAddExtraBaggage,
  });
}

export function pushFlightConditionsEvent({
  itinerary,
  parsedSearchQuery,
  $resources,
  eventName,
}: {
  itinerary: ItineraryModel | DomesticItinerary;
  parsedSearchQuery: ParsedSearchQuery;
  $resources: FlightInfoStore["$resources"];
  eventName: string;
}) {
  const { isOneWay, adult, child, infant, isRoundTrip, pax, cabin } =
    parsedSearchQuery;
  const {
    id,
    legs,
    uniqueCarriersArr,
    hasMixedCabin = false,
    isHajjUmrahFlight = false,
    hasAirportChange = false,
    cabinClassesIncluded,
    isSingleCabinTrip,
    searchRequest,
  } = itinerary || {};

  const { departureTime: returnTime, departure: returnDate } = !isOneWay
    ? legs[legs.length - 1]
    : ({} as LegModel);
  const { originId, destinationId } = legs[0] || {};
  const airlineName = _join(uniqueCarriersArr, ",");
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");

  const tripCabin = cabinClassesIncluded[0];

  const availableFlightConditions = isHajjUmrahFlight
    ? ["HajjUmrahFlight"]
    : [];
  hasMixedCabin && availableFlightConditions.push("MixedCabin");
  hasAirportChange && availableFlightConditions.push("AirportChange");
  const cabinClass = isSingleCabinTrip ? tripCabin : cabin;

  return pushDataLayerWithParams<
    | FlightsFlightConditionsAcceptedEventParams
    | FlightsFlightConditionsEventParams
    | FlightsFlightConditionsViewedEventParams
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: eventName,
    origin_iata: originId,
    destination_iata: destinationId,
    flight_number: _join(_uniq(_map(legs, (leg) => leg.flightCode)), ","),
    search_id: id,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_countryName_en: $resources.getAirportCountry(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    destination_countryName_en: $resources.getAirportCountry(
      destinationId,
      LANGUAGE.EN,
    ),
    cabin_class: cabinClass,
    flight_type: searchRequest?.isDomesticKSA ? "dom" : "int",
    trip_type: isRoundTrip ? "roundtrip" : "oneway",
    ...(isRoundTrip && !!returnDate && { return_date: returnDate }),
    ...(returnTime && {
      return_time: format(new Date(returnTime), TIME_FORMAT_24),
    }),
    airline_iata: airlineName,
    airline_name_en: airlineNameEn,
    origin_airportName_en: $resources.getAirportLabel(originId, LANGUAGE.EN),
    destination_airportName_en: $resources.getAirportLabel(
      destinationId,
      LANGUAGE.EN,
    ),
    checkin_baggage: hasMixedCabin,
    adult_pax: adult,
    child_pax: child,
    infant_pax: infant,
    total_pax: pax,
    price: getTotalEventPrice(itinerary, isRoundTrip),
    available_flight_conditions:
      availableFlightConditions.length > 0
        ? JSON.stringify(availableFlightConditions)
        : "",
  });
}

export function pushFlightSearchResultsLoadedEvent({
  parsedSearchQuery,
  isDomestic,
  $resources,
  isDirectFlight,
}: {
  parsedSearchQuery: ParsedSearchQuery;
  isDomestic: boolean;
  $resources: FlightInfoStore["$resources"];
  isDirectFlight: boolean;
}) {
  const {
    adult,
    child,
    infant,
    departureDate,
    destinationId,
    returnDate,
    originId,
    isRoundTrip,
    pax,
    cabin,
    isMultiCity,
  } = parsedSearchQuery;

  const tripType = isRoundTrip ? "roundtrip" : "oneway";

  return pushDataLayerWithParams<FlightsSrpPageLoadedEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_RSP_PAGE_LOADED,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_countryName_en: $resources.getAirportCountry(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    destination_countryName_en: $resources.getAirportCountry(
      destinationId,
      LANGUAGE.EN,
    ),
    cabin_class: cabin,
    flight_type: isDomestic ? "dom" : "int",
    trip_type: isMultiCity ? "multicity" : tripType,
    departure_date: departureDate,
    direct_flight: isDirectFlight,
    adult_pax: adult,
    total_pax: pax,
    infant_pax: infant,
    child_pax: child,
    ...(isRoundTrip && !!returnDate && { return_date: returnDate }),
  });
}

export function pushFlightResultCardViewdEvent({
  isGroup,
  itinerary,
  index,
  $resources,
  eventName = AVO_EVENT_NAMES.FLIGHTS_RSP_CARD_VIEWED,
}: {
  isGroup: boolean;
  itinerary: ItineraryModel | DomesticItinerary;
  index: number;
  $resources: FlightInfoStore["$resources"];
  eventName?: string;
}) {
  const { id, uniqueCarriersArr, legs } = (itinerary ||
    {}) as DomesticItinerary;
  const { displayPrice: priceProps, seatsAvailable } = (itinerary ||
    {}) as ItineraryModel;
  const {
    stops,
    departureTime,
    arrivalTime,
    departure,
    arrival,
    originId,
    destinationId,
    durationInSeconds,
  } = legs[0] || {};

  const { total } = priceProps || {};
  const displayTotal = total?.replace(/[,\s]/, "");
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");
  const seatsToSend = isSeatsRemainingThreshold(seatsAvailable, false)
    ? seatsAvailable?.toString()
    : "unavailable";

  return pushDataLayerWithParams<
    | FlightsSrpFlightCardViewedEventParams
    | FlightsSrpInboundFlightCardViewedEventParams
    | FlightsSrpOutboundFlightCardViewedEventParams
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: eventName,
    result_stacked: isGroup,
    total_duration: durationInSeconds / 60,
    search_id: id,
    departure_datetime_utc: format(
      asUTC(new Date(departureTime)),
      DATE_WITH_TIME_FORMAT,
    ),
    departure_datetime_local: format(
      new Date(departureTime),
      DATE_WITH_TIME_FORMAT,
    ),
    arrival_datetime_local: format(
      new Date(arrivalTime),
      DATE_WITH_TIME_FORMAT,
    ),
    arrival_date: arrival,
    departure_date: departure,
    price: parseFloat(displayTotal),
    origin_airport_code: originId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_cityName_local: $resources.getAirportCity(originId),
    destination_airport_code: destinationId,
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    airline_name_en: airlineNameEn,
    flight_number: _join(_uniq(_map(legs, (leg) => leg.flightCode)), ","),
    card_position: index,
    stops: stops.length,
    seats_remaining: String(seatsToSend),
  });
}

export function pushFlightDetailsBaggageRuleEvent({
  hasCheckinBaggage,
  hasCabinBaggage,
  page,
}: {
  hasCheckinBaggage: boolean;
  hasCabinBaggage: boolean;
  page: string;
}) {
  return pushDataLayerWithParams<FlightsDpBaggageEventParams>({
    event:
      FLIGHT_DETAILS_PAGE_EVENT_MAP[page] || AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_DETAILS,
    event_name: "flights_dp_baggage",
    checkin_baggage: hasCheckinBaggage,
    cabin_baggage: hasCabinBaggage,
  });
}

export function pushFlightDetailsFareRuleEvent({
  fareRule,
  page,
}: {
  fareRule: FareRulesModel;
  page: string;
}) {
  const { cancellationMap, changeMap } = fareRule || {};

  const filterHandler = (_, key) => {
    return key.includes(TRAVELLERS_TYPES_CODES_MAP.ADT);
  };

  const cancellationFee =
    _find(cancellationMap, filterHandler) || ({} as FareRuleModel);
  const dateChangeFee =
    _find(changeMap, filterHandler) || ({} as FareRuleModel);
  return pushDataLayerWithParams<FlightsDpFareRulesEventParams>({
    event:
      FLIGHT_DETAILS_PAGE_EVENT_MAP[page] || AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_DETAILS,
    event_name: "flights_dp_fare_rules",
    cancellation_fee: cancellationFee.amount,
    date_change_fee: dateChangeFee.amount,
  });
}

export function pushAddAddonEvent({
  saleId,
  addonType,
  salePrice,
  addOnPrice,
  page: currentPage = APP_PAGES.FLIGHTS_PAX,
}: {
  saleId: string;
  addonType: string;
  salePrice: string;
  addOnPrice: string;
  page?: string;
}) {
  const isPaxPage = currentPage === APP_PAGES.FLIGHTS_PAX;

  let event = AVO_EVENTS.FLIGHT_PAX;
  let page = "travellers";
  if (!isPaxPage) {
    event = AVO_EVENTS.FLIGHT_EXTRAS;
    page = "addons";
  }
  return pushDataLayerWithParams<FlightsAddonAddedEventParams>({
    event,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS,
    event_name: "flights_addon_added",
    sale_id: saleId,
    addon_type: addonType,
    addon_price: addOnPrice,
    sale_price: salePrice,
    page,
  });
}

export function pushRemoveAddonEvent({
  saleId,
  addonType,
  addOnPrice,
  page: currentPage = APP_PAGES.FLIGHTS_PAX,
}: {
  saleId: string;
  addonType: string;
  addOnPrice: string;
  page?: string;
}) {
  const isPaxPage = currentPage === APP_PAGES.FLIGHTS_PAX;

  let event = AVO_EVENTS.FLIGHT_PAX;
  let page = "travellers";
  if (!isPaxPage) {
    event = AVO_EVENTS.FLIGHT_EXTRAS;
    page = "addons";
  }

  return pushDataLayerWithParams<FlightsAddonRemovedEventParams>({
    event,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS,
    event_name: "flights_addon_removed",
    sale_id: saleId,
    addon_type: addonType,
    addon_price: addOnPrice,
    page,
  });
}

export function pushPreBookingPageLoadedEvent({
  parsedSearchQuery,
  isDomestic,
  $resources,
  isPaxPage,
  flightProduct,
  availableAddOns,
  $currency,
  saleDisplaytotal,
  rules,
  isPriceJumpActive = false,
}: {
  flightProduct: FlightProductModel;
  parsedSearchQuery: ParsedSearchQuery;
  isDomestic: boolean;
  userId: string;
  language: Language;
  currency: string;
  isLoggedIn: boolean;
  $resources: FlightInfoStore["$resources"];
  isPaxPage: boolean;
  availableAddOns: AddOnsModel[];
  $currency: CurrencyStore;
  saleDisplaytotal: any;
  rules: RuleProduct[];
  isPriceJumpActive: boolean;
}) {
  const action = isPaxPage
    ? "flights_gp_pageLoaded"
    : "flights_addon_pageLoaded";
  const event = isPaxPage ? AVO_EVENTS.FLIGHT_PAX : AVO_EVENTS.FLIGHT_EXTRAS;
  const category = isPaxPage
    ? AVO_EVENT_CATEGORIES.FLIGHTS_TRAVELER_DETAILS
    : AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS;

  const { itinerary, baggageInfo, luggageInfo } = flightProduct;
  const {
    price: priceProps,
    legs,
    isMultiPNR,
    uniqueCarriersArr,
    isSingleCabinTrip,
    cabinClassesIncluded,
  } = itinerary;
  const {
    adult,
    child,
    infant,
    isRoundTrip,
    pax,
    cabin,
    isOneWay,
    isMultiCity,
  } = parsedSearchQuery;
  const { stops, departureTime, departure, arrival, originId, destinationId } =
    legs[0] || ({} as LegModel);
  const { departureTime: returnTime, departure: returnDate } = !isOneWay
    ? legs[legs.length - 1]
    : ({} as LegModel);
  const { fee } = priceProps || ({} as ItineraryPrice<string>);
  const canAddExtraBaggage = !!luggageInfo[0]?.options?.length;
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join("|");
  const flightCode = _join(_uniq(_map(legs, (leg) => leg.flightCode)), "|");
  const airlineName = uniqueCarriersArr?.join("|");
  const departureDate = `${format(
    new Date(departure),
    DATE_WITH_TIME_FORMAT,
  )}:00`;
  const arrivalDate = `${format(new Date(arrival), DATE_WITH_TIME_FORMAT)}:00`;
  const seatsAvailable = _get(itinerary, "seatsAvailable");

  const isPriceUpdated = _get(
    flightProduct.additionalData,
    "_priceCompareResult.isPriceUpdated",
    null,
  );
  const priceDiff = _get(
    flightProduct.additionalData,
    "_priceCompareResult.priceDiff",
    null,
  );

  const differentCarrier = itinerary.uniqueCarriersArr.length > 1 && isMultiPNR;
  const tripCabin = cabinClassesIncluded[0];
  const cabinClass = isSingleCabinTrip ? tripCabin : cabin;

  const tripType = isRoundTrip ? "roundtrip" : "oneway";
  const addonNames = _map(availableAddOns, (addon) => addon.name).join("|");

  const extraProperties = {
    price_jump_updated:
      isPriceJumpActive && isPriceUpdated ? isPriceUpdated : null,
    price_jump_difference: priceDiff ? priceDiff : null,
  };

  const addonPageData: EventData<FlightsAddonPageLoadedEventParams> = {
    event,
    event_category: category,
    event_name: action,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_countryName_en: $resources.getAirportCountry(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    destination_countryName_en: $resources.getAirportCountry(
      destinationId,
      LANGUAGE.EN,
    ),
    origin_airportName_en: $resources.getAirportLabel(originId, LANGUAGE.EN),
    destination_airportName_en: $resources.getAirportLabel(
      destinationId,
      LANGUAGE.EN,
    ),
    cabin_class: cabinClass,
    flight_type: isDomestic ? "dom" : "int",
    trip_type: isMultiCity ? "multicity" : tripType,
    departure_date: departureDate,
    adult_pax: adult,
    total_pax: pax,
    infant_pax: infant,
    child_pax: child,
    departure_datetime_local: departureDate,
    arrival_datetime_local: arrivalDate,
    airline_iata: airlineName,
    airline_name_en: airlineNameEn,
    flight_number: flightCode,
    price: saleDisplaytotal?.total,
    direct_flight: !stops.length,
    arrival_date: arrivalDate,
    departure_time: format(new Date(departureTime), TIME_FORMAT_24),
    checkin_baggage: !!baggageInfo[0]?.freeBaggage?.checkIn?.isIncluded,
    extra_baggage: canAddExtraBaggage,
    return_time:
      returnTime && format(new Date(returnTime), DATE_WITH_TIME_FORMAT),
    available_addons: addonNames,
    is_multi_pnr: differentCarrier,
    price_sar: $currency?.convertPriceToCurrency(
      Number(saleDisplaytotal?.total),
      SAR,
    ),
    ...(isRoundTrip &&
      !!returnDate && {
        return_date: `${format(
          new Date(returnDate),
          DATE_WITH_TIME_FORMAT,
        )}:00`,
      }),
    ...(returnTime && {
      return_time: format(new Date(returnTime), TIME_FORMAT_24),
    }),
    ...(isPaxPage && {
      seats_remaining:
        Number(seatsAvailable) > 0 ? String(seatsAvailable) : "unavailable",
    }),
    ...(!isPaxPage && { ...extraProperties }),
    // @ts-ignore
    route: `${originId}-${destinationId}`,
  };

  const paxPageData: EventData<FlightsGpPageLoadedEventParams> = {
    ...addonPageData,
    service_fee: saleDisplaytotal?.markup || fee,
    service_fee_code: _join(
      _map(
        _filter(rules, (rule) => rule.isServiceFee),
        (rule) => rule.code,
      ),
      "|",
    ),
    frequent_flyer_available: _some(
      flightProduct?.travellers,
      (traveller: TravellerModel) => traveller.hasFrequentFlyerOptions,
    ),
  };

  return pushDataLayerWithParams<
    FlightsAddonPageLoadedEventParams | FlightsGpPageLoadedEventParams
  >(isPaxPage ? paxPageData : addonPageData);
}

export function pushFinalDetailsClickedEvent({
  saleId,
  flightProduct,
  page,
}: {
  saleId: string;
  flightProduct: FlightProductModel;
  page: string;
}) {
  const isPaxPage = page === APP_PAGES.FLIGHTS_PAX;
  const event = isPaxPage ? AVO_EVENTS.FLIGHT_PAX : AVO_EVENTS.FLIGHT_EXTRAS;
  const category = isPaxPage
    ? AVO_EVENT_CATEGORIES.FLIGHTS_TRAVELER_DETAILS
    : AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS;

  return pushDataLayerWithParams<FlightsGpFinalDetailsClickedEventParams>({
    event,
    event_category: category,
    event_name: "flights_gp_finalDetails_clicked",
    search_id: flightProduct?.itinerary?.id,
    sale_id: saleId,
    frequent_flyer_added: _some(
      flightProduct?.ssr,
      (ssrItem: SSRModel) => !!ssrItem?.frequentFlyer?.number,
    ),
  });
}

export function pushFlightDetailsLoadedEvent({
  parsedSearchQuery,
  isDomestic,
  $resources,
  page,
  itinerary,
  baggageInfo,
  isFareFamilyUpgraded,
}: {
  itinerary: ItineraryModel | DomesticItinerary;
  parsedSearchQuery: ParsedSearchQuery;
  isDomestic: boolean;
  language: Language;
  $resources: FlightInfoStore["$resources"];
  page: string;
  baggageInfo: LegBaggageInfo[];
  isFareFamilyUpgraded: boolean;
}) {
  const event = FLIGHT_DETAILS_PAGE_EVENT_MAP[page] || AVO_EVENTS.FLIGHT_SEARCH;

  const {
    displayPrice,
    legs,
    isMultiPNR,
    uniqueCarriersArr,
    direction,
    cabinClassesIncluded,
    isSingleCabinTrip,
  } = itinerary as ItineraryModel;
  const { displayCombinedPrice } = itinerary as DomesticItinerary;

  const {
    adult,
    child,
    infant,
    isRoundTrip,
    pax,
    cabin,
    isOneWay,
    isMultiCity,
  } = parsedSearchQuery;
  const {
    stops,
    departureTime,
    arrivalTime,
    departure,
    arrival,
    originId,
    destinationId,
  } = legs[0] || ({} as LegModel);
  const { departureTime: returnTime, departure: returnDate } = !isOneWay
    ? legs[legs.length - 1]
    : ({} as LegModel);
  const priceProps = displayCombinedPrice || displayPrice;
  const { total, roundtripTotal, cheapestRoundtripTotal } =
    priceProps || ({} as ItineraryPrice<string>);
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");
  const flightCode = _join(_uniq(_map(legs, (leg) => leg.flightCode)), ",");
  const airlineName = uniqueCarriersArr?.join(",");
  const tripType = isRoundTrip ? "roundtrip" : "oneway";
  const isOutbound = direction === "outbound";
  const price =
    isRoundTrip && isOutbound
      ? cheapestRoundtripTotal?.replaceAll(",", "")
      : roundtripTotal;

  const tripCabin = cabinClassesIncluded[0];
  const cabinClass = isSingleCabinTrip ? tripCabin : cabin;

  const displayTotal = String(isOneWay ? total : price)?.replace(/[,\s]/, "");

  return pushDataLayerWithParams<FlightsDpPageLoadedEventParams>({
    event,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_DETAILS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_DETAILS_PAGE_LOADED,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_countryName_en: $resources.getAirportCountry(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    destination_countryName_en: $resources.getAirportCountry(
      destinationId,
      LANGUAGE.EN,
    ),
    origin_airportName_en: $resources.getAirportLabel(originId, LANGUAGE.EN),
    destination_airportName_en: $resources.getAirportLabel(
      destinationId,
      LANGUAGE.EN,
    ),
    cabin_class: cabinClass,
    flight_type: isDomestic ? "dom" : "int",
    trip_type: isMultiCity ? "multicity" : tripType,
    departure_date: departure,
    adult_pax: adult,
    total_pax: pax,
    infant_pax: infant,
    child_pax: child,
    departure_datetime_local: format(
      new Date(departureTime),
      DATE_WITH_TIME_FORMAT,
    ),
    arrival_datetime_local: format(
      new Date(arrivalTime),
      DATE_WITH_TIME_FORMAT,
    ),
    airline_iata: airlineName,
    airline_name_en: airlineNameEn,
    flight_number: flightCode,
    price: parseFloat(displayTotal),
    direct_flight: !stops.length,
    arrival_date: arrival,
    departure_time: format(new Date(departureTime), TIME_FORMAT_24),
    checkin_baggage: !!baggageInfo[0]?.freeBaggage?.checkIn?.isIncluded,
    extra_baggage: false,
    is_multi_pnr: isMultiPNR,
    fare_family_upgraded: isFareFamilyUpgraded,
    // @ts-ignore
    route: `${originId}-${destinationId}`,
    ...(isRoundTrip && !!returnDate && { return_date: returnDate }),
    ...(returnTime && {
      return_time: format(new Date(returnTime), TIME_FORMAT_24),
    }),
  });
}

export function pushMetaFareFamiliesEvent({
  eventName,
  $resources,
  flightProduct,
  availableAddOns,
  selectedBrandedFare,
  cardPosition,
  price,
  numberOfCards,
}: {
  eventName: string;
  flightProduct: FlightProductModel;
  $resources: FlightInfoStore["$resources"];
  availableAddOns: AddOnsModel[];
  selectedBrandedFare: BrandedFaresModel;
  cardPosition: number;
  price: number;
  numberOfCards: number;
}) {
  const payload = getMetaFareFamiliesPayload(
    eventName,
    flightProduct,
    $resources,
    availableAddOns,
    selectedBrandedFare,
    cardPosition,
    price,
    numberOfCards,
  );

  switch (eventName) {
    case EVENTS.META_FARE_FAMILIES_UPGRADED:
    case EVENTS.META_FARE_FAMILIES_SELECTED: {
      const {
        event,
        event_name,
        event_category,
        price,
        checkin_baggage,
        extra_baggage,
        cancellation_availability,
        cancellation_fee,
        datechange_fee,
        card_position,
        number_of_cards,
        datechange_availability,
      } = payload;
      return pushDataLayerWithParams<
        | FlightsFareFamiliesSelectedEventParams
        | FlightsFareFamiliesUpgradedEventParams
      >({
        event,
        event_name,
        event_category,
        price,
        checkin_baggage,
        extra_baggage,
        cancellation_availability,
        cancellation_fee: _toString(cancellation_fee),
        date_change_fee: _toString(datechange_fee),
        card_position,
        number_of_cards,
        datechange_availability,
      });
    }
    case EVENTS.META_FARE_FAMILIES_FAILED:
    case EVENTS.META_FARE_FAMILIES_RELOADED: {
      const {
        event,
        event_name,
        event_category,
        search_id,
        origin_iata,
        destination_iata,
        flight_number,
      } = payload;
      return pushDataLayerWithParams<
        | FlightsFareFamiliesReloadedEventParams
        | FlightsFareFamiliesErrorEventParams
      >({
        event,
        event_name,
        event_category,
        search_id,
        origin_iata,
        destination_iata,
        flight_number,
      });
    }
    case EVENTS.META_FARE_FAMILIES_SCROLL: {
      const { event, event_name, event_category, number_of_cards } = payload;
      return pushDataLayerWithParams<FlightsFareFamiliesScrollEventParams>({
        event,
        event_name,
        event_category,
        number_of_cards,
      });
    }
    default:
      return pushDataLayerWithParams(payload);
  }
}

const getMetaFareFamiliesPayload = (
  event_name: string,
  flightProduct: FlightProductModel,
  $resources: FlightInfoStore["$resources"],
  availableAddOns: AddOnsModel[],
  selectedBrandedFare: BrandedFaresModel,
  cardPosition: number,
  price: number,
  numberOfCards: number,
) => {
  const { luggageInfo, itinerary } = flightProduct;
  const {
    legs,
    itineraryDuration,
    searchRequest,
    id,
    uniqueCarriersArr,
    isSingleCabinTrip,
    cabinClassesIncluded,
  } = itinerary;
  const parsedSearchQuery = getParsedSearchQuery(searchRequest.query);
  const {
    adult,
    child,
    infant,
    isRoundTrip,
    pax,
    cabin,
    isOneWay,
    isMultiCity,
  } = parsedSearchQuery;
  const {
    stops,
    departureTime,
    arrivalTime,
    departure,
    arrival,
    originId,
    destinationId,
  } = legs[0] || ({} as LegModel);
  const { departureTime: returnTime, departure: returnDate } = !isOneWay
    ? legs[legs.length - 1]
    : ({} as LegModel);
  const canAddExtraBaggage = !!luggageInfo[0]?.options?.length;
  const cancellation_availability =
    selectedBrandedFare?.cancellation?.isAvailable;
  const cancellation_fee = selectedBrandedFare?.cancellation?.price;
  const datechange_availability =
    selectedBrandedFare?.flightChange?.isAvailable;
  const datechange_fee = selectedBrandedFare?.flightChange?.price;
  const checkin_baggage = selectedBrandedFare?.checkInBaggage?.isIncluded;
  const airlineName = _join(uniqueCarriersArr, ",");
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");
  const tripType = isRoundTrip ? "roundtrip" : "oneway";
  const tripCabin = cabinClassesIncluded[0];
  const cabinClass = isSingleCabinTrip ? tripCabin : cabin;

  return {
    search_id: id,
    event_name,
    event: AVO_EVENTS.FLIGHT_PAX,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_TRAVELER_DETAILS,
    cancellation_availability,
    cancellation_fee,
    datechange_availability,
    datechange_fee,
    total_duration: itineraryDuration,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_countryName_en: $resources.getAirportCountry(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    destination_countryName_en: $resources.getAirportCountry(
      destinationId,
      LANGUAGE.EN,
    ),
    origin_airportName_en: $resources.getAirportLabel(originId, LANGUAGE.EN),
    destination_airportName_en: $resources.getAirportLabel(
      destinationId,
      LANGUAGE.EN,
    ),
    cabin_class: cabinClass,
    flight_type: searchRequest?.isDomesticKSA ? "dom" : "int",
    trip_type: isMultiCity ? "multicity" : tripType,
    departure_date: departure,
    adult_pax: adult,
    total_pax: pax,
    infant_pax: infant,
    child_pax: child,
    departure_datetime_local: format(
      new Date(departureTime),
      DATE_WITH_TIME_FORMAT,
    ),
    arrival_datetime_local: format(
      new Date(arrivalTime),
      DATE_WITH_TIME_FORMAT,
    ),
    airline_iata: airlineName,
    airline_name_en: airlineNameEn,
    flight_number: _join(_uniq(_map(legs, (leg) => leg.flightCode)), ","),
    price,
    direct_flight: !stops.length,
    arrival_date: arrival,
    departure_time: format(new Date(departureTime), TIME_FORMAT_24),
    arrival_time: format(new Date(arrivalTime), TIME_FORMAT_24),
    checkin_baggage,
    extra_baggage: canAddExtraBaggage,
    available_addons: JSON.stringify(availableAddOns),
    ...(cardPosition && { card_position: cardPosition }),
    ...(numberOfCards && { number_of_cards: numberOfCards }),
    ...(returnTime && {
      return_time: format(new Date(returnTime), TIME_FORMAT_24),
    }),
    ...(returnDate && { return_date: returnDate }),
  };
};

export function pushAddonBenefitsViewedEvent({
  saleId,
  addonType,
  addOnPrice,
  salePrice,
  page: currentPage = APP_PAGES.FLIGHTS_PAX,
}: {
  saleId: string;
  addonType: string;
  addOnPrice: string;
  salePrice: string;
  page: string;
}) {
  let page = "travellers";
  let event = AVO_EVENTS.FLIGHT_PAX;

  if (currentPage !== APP_PAGES.FLIGHTS_PAX) {
    page = "addons";
    event = AVO_EVENTS.FLIGHT_EXTRAS;
  }
  return pushDataLayerWithParams<FlightsAddonBenefitsViewedEventParams>({
    event,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS,
    event_name: "flights_addon_benefits_viewed",
    sale_id: saleId,
    addon_type: addonType,
    addon_price: addOnPrice,
    sale_price: salePrice,
    page,
  });
}

export function pushRecaptureAddonEvent({
  eventName,
  addonData,
  saleId,
}: {
  eventName: string;
  addonData: Partial<AddOnsModel>;
  saleId: string;
}) {
  return pushDataLayerWithParams<
    | FlightsAddonRecaptureSkippedEventParams
    | FlightsAddonRecaptureAddedEventParams
    | FlightsAddonRecaptureViewedEventParams
  >({
    page: "addons",
    sale_id: saleId,
    sale_price: addonData?.totalPrice,
    event_name: eventName,
    addon_type: addonData?.type,
    event: AVO_EVENTS.FLIGHT_EXTRAS,
    addon_price: addonData?.oldPrice,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS,
  });
}

export function pushStepFlightCardViewdEvent({
  isGroup,
  itinerary,
  index,
  $resources,
}: {
  isGroup: boolean;
  itinerary: ItineraryModel;
  index: number;
  $resources: FlightInfoStore["$resources"];
}) {
  const { direction } = itinerary || {};
  const eventName =
    direction === INBOUND
      ? AVO_EVENT_NAMES.FLIGHTS_SRP_INBOUND_CARD_VIEWED
      : AVO_EVENT_NAMES.FLIGHTS_SRP_OUTBOUND_CARD_VIEWED;
  pushFlightResultCardViewdEvent({
    isGroup,
    itinerary,
    index,
    $resources,
    eventName,
  });
}

export function pushStepFlightSelectedEvent({
  itinerary,
  $resources,
  fareFamilyUpgraded,
  index,
  hasGroup,
}: {
  itinerary: ItineraryModel;
  $resources: FlightInfoStore["$resources"];
  fareFamilyUpgraded: boolean;
  index: number;
  hasGroup: boolean;
}) {
  const {
    price: itineraryPrice,
    id,
    direction,
    uniqueCarriersArr,
    seatsAvailable,
    searchRequest,
    isSingleCabinTrip,
    cabinClassesIncluded,
  } = itinerary || {};
  const {
    originId,
    destinationId,
    totalPax,
    pax,
    searchType,
    preferredCabin,
    isDomesticKSA,
  } = searchRequest || {};
  const isOutbound = direction === OUTBOUND || searchRequest.isOneWay;
  const eventName = isOutbound
    ? AVO_EVENT_NAMES.FLIGHTS_SRP_OUTBOUND_CARD_SELECTED
    : AVO_EVENT_NAMES.FLIGHTS_SRP_INBOUND_CARD_SELECTED;
  const { cheapestRoundtripTotal, roundtripTotal, total } =
    itineraryPrice || {};
  const totalPrice = roundtripTotal || cheapestRoundtripTotal || total;
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");
  const seatsToSend = isSeatsRemainingThreshold(seatsAvailable, false)
    ? seatsAvailable?.toString()
    : "unavailable";
  const tripCabin = cabinClassesIncluded[0];
  const cabinClass = isSingleCabinTrip ? tripCabin : preferredCabin;

  return pushDataLayerWithParams<
    | FlightsSrpInboundFlightSelectedEventParams
    | FlightsSrpOutboundFlightSelectedEventParams
    | any
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: eventName,
    search_id: id,
    price: totalPrice,
    airline_name_en: airlineNameEn,
    fare_family_upgraded: fareFamilyUpgraded,
    card_position: index,
    seats_remaining: String(seatsToSend),
    grouped_flight: hasGroup,
    origin_iata: originId,
    destination_iata: destinationId,
    cabin_class: cabinClass,
    flight_type: isDomesticKSA ? "dom" : "int",
    trip_type: searchType,
    adult_pax: pax.adult,
    total_pax: totalPax,
    infant_pax: pax.infant,
    airline_iata: airlineNameEn,
    child_pax: typeof pax === "object" && "child" in pax ? pax.child : 0,
  });
}

export function pushSameAirlineFilterEvent({
  isEnabled,
  airlineNameEn,
  airlineCode,
}: {
  isEnabled: boolean;
  airlineNameEn: string;
  airlineCode: string;
}) {
  return pushDataLayerWithParams<FlightsSrpSameAirlineFilterEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_SRP_SAME_AIRLINE_FILTER,
    filter_values: isEnabled ? "on" : "off",
    airline_name_en: airlineNameEn,
    airline_iata: airlineCode,
  });
}

export function pushAddFFEvent({
  saleId,
  itineraryId,
  airlineNameEn,
  airlineCode,
  isMultiPNR,
}: {
  saleId: string;
  itineraryId: string;
  airlineNameEn: string;
  airlineCode: string;
  isMultiPNR: boolean;
}) {
  return pushDataLayerWithParams<FlightsGpFrequentFlyerAddedEventParams>({
    event: AVO_EVENTS.FLIGHT_PAX,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_TRAVELER_DETAILS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_GP_FF_ADDED,
    search_id: itineraryId,
    sale_id: saleId,
    airline_iata: airlineCode,
    airline_name_en: airlineNameEn,
    is_multi_pnr: isMultiPNR,
  });
}

export function pushAddExtraBaggageEvent({
  saleId,
  itinerary,
  isMultiPNR,
  $resources,
}: {
  saleId: string;
  itinerary: ItineraryModel;
  isMultiPNR: boolean;
  $resources: FlightInfoStore["$resources"];
}) {
  const { uniqueCarriersArr } = itinerary;
  const airlineName = _join(uniqueCarriersArr, ",");
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");

  return pushDataLayerWithParams<FlightsGpExtraBaggageAddedEventParams>({
    event: AVO_EVENTS.FLIGHT_PAX,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_TRAVELER_DETAILS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_GP_BAGGAGE_ADDED,
    search_id: itinerary.id,
    sale_id: saleId,
    airline_iata: airlineName,
    airline_name_en: airlineNameEn,
    is_multi_pnr: isMultiPNR,
  });
}

export function pushNearbyAirportsCardViewed({
  originId = "",
  destinationId = "",
  $resources,
  flightDirection,
  nearbyId,
}: {
  originId: string;
  destinationId: string;
  $resources: FlightInfoStore["$resources"];
  flightDirection: string;
  nearbyId: string;
}) {
  return pushDataLayerWithParams<FlightsSrpNearbyAirportsViewEventParams>({
    event_name: "flights_srp_nearbyAirports_view",
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    nearby_iata: nearbyId,
    nearby_cityname_en: $resources.getAirportCity(nearbyId, LANGUAGE.EN),
    flight_direction: flightDirection,
  });
}

export function pushNearbyAirportsCardClicked({
  originId = "",
  destinationId = "",
  $resources,
  shouldHide,
  flightDirection,
  nearbyId,
}: {
  originId: string;
  destinationId: string;
  $resources: FlightInfoStore["$resources"];
  shouldHide: boolean;
  flightDirection: string;
  nearbyId: string;
}) {
  return pushDataLayerWithParams<FlightsSrpNearbyAirportsClickEventParams>({
    event_name: "flights_srp_nearbyAirports_click",
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId),
    destination_cityName_en: $resources.getAirportCity(destinationId),
    nearbyairports_banner_selection: shouldHide ? "No" : "Yes",
    nearby_iata: nearbyId,
    nearby_cityname_en: $resources.getAirportCity(nearbyId, LANGUAGE.EN),
    flight_direction: flightDirection,
  });
}

export function pushAddonSkippedEvent({
  parsedSearchQuery,
  isDomestic,
  $resources,
  flightProduct,
  availableAddOns,
}: {
  flightProduct: FlightProductModel;
  parsedSearchQuery: ParsedSearchQuery;
  isDomestic: boolean;
  $resources: FlightInfoStore["$resources"];
  availableAddOns: AddOnsModel[];
}): void {
  const { itinerary, baggageInfo, luggageInfo } = flightProduct;
  const {
    displayPrice: priceProps,
    legs,
    isMultiPNR,
    uniqueCarriersArr,
    isSingleCabinTrip,
    cabinClassesIncluded,
  } = itinerary;
  const {
    adult,
    child,
    infant,
    isRoundTrip,
    pax,
    cabin,
    isOneWay,
    isMultiCity,
  } = parsedSearchQuery;
  const { stops, departureTime, arrivalTime, originId, destinationId } =
    legs?.[0] || ({} as LegModel);
  const { departureTime: returnTime, departure: returnDate } = !isOneWay
    ? legs[legs.length - 1]
    : ({} as LegModel);
  const { total, roundtripTotal } =
    priceProps || ({} as ItineraryPrice<string>);
  const canAddExtraBaggage = !!luggageInfo[0]?.options?.length;
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");
  const flightCode = _join(_uniq(_map(legs, (leg) => leg.flightCode)), ",");
  const airlineName = uniqueCarriersArr?.join(",");
  const tripType = isRoundTrip ? "roundtrip" : "oneway";
  const tripCabin = cabinClassesIncluded[0];
  const cabinClass = isSingleCabinTrip ? tripCabin : cabin;
  const displayTotal = String(isMultiPNR ? roundtripTotal : total)?.replace(
    /[,\s]/,
    "",
  );

  return pushDataLayerWithParams<FlightsAddonSkippedEventParams>({
    event: AVO_EVENTS.FLIGHT_EXTRAS,
    event_name: "flights_addon_skipped",
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_countryName_en: $resources.getAirportCountry(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    destination_countryName_en: $resources.getAirportCountry(
      destinationId,
      LANGUAGE.EN,
    ),
    origin_airportName_en: $resources.getAirportLabel(originId, LANGUAGE.EN),
    destination_airportName_en: $resources.getAirportLabel(
      destinationId,
      LANGUAGE.EN,
    ),
    cabin_class: cabinClass,
    flight_type: isDomestic ? "dom" : "int",
    trip_type: isMultiCity ? "multicity" : tripType,
    adult_pax: adult,
    total_pax: pax,
    infant_pax: infant,
    child_pax: child,
    departure_datetime_local: format(
      new Date(departureTime),
      DATE_WITH_TIME_FORMAT,
    ),
    arrival_datetime_local: format(
      new Date(arrivalTime),
      DATE_WITH_TIME_FORMAT,
    ),
    airline_iata: airlineName,
    airline_name_en: airlineNameEn,
    flight_number: flightCode,
    price: parseFloat(displayTotal),
    direct_flight: !stops.length,
    departure_time: format(new Date(departureTime), TIME_FORMAT_24),
    checkin_baggage: !!baggageInfo[0]?.freeBaggage?.checkIn?.isIncluded,
    extra_baggage: canAddExtraBaggage,
    return_time: returnTime && format(new Date(returnTime), TIME_FORMAT_24),
    available_addons: JSON.stringify(availableAddOns),
    ...(isRoundTrip && !!returnDate && { return_date: returnDate }),
    ...(returnTime && {
      return_time: format(new Date(returnTime), TIME_FORMAT_24),
    }),
  });
}

export function pushItemNotFoundEvent(flightErrorMessage = "") {
  pushDataLayer({
    event: "google_flight_view_item",
    flight_error_code: "FLIGHT_NOT_FOUND",
    flight_error_message: flightErrorMessage,
  });
}

export function pushViewItemEvent(displayedTotal, currency) {
  pushDataLayer({
    event: "google_flight_view_item",
    value: displayedTotal,
    currency,
  });
}

export function pushFlightConfirmationEvent(flightProduct, total, currency) {
  const tripType =
    _get(flightProduct, "additionalData.searchType", "") === "MultiCity"
      ? "Other"
      : _get(flightProduct, "additionalData.searchType", "");

  const legItems = _get(flightProduct, "itinerary.legs", []).map(
    (leg, index) => {
      return {
        travel_class: _get(
          flightProduct,
          `options.fare.legDetails[${index}].segment[0].cabinText`,
          "",
        ),
        fare_product: _get(leg, "segments[0].fareFamily", ""),
        booking_code: _get(leg, "segments[0].additionalDetails.cabinCode", ""),
        flight_number: leg.flightCode.replace("-", ""),
        origin: leg.originId,
        destination: leg.destinationId,
        start_date: _get(leg, "legSummary.departure", "").split("T")[0],
      };
    },
  );

  pushDataLayer({
    event: "google_flight_confirmation",
    value: total || "",
    currency: currency || "",
    trip_type: tripType,
    passengers_total:
      _get(flightProduct, "additionalData.paxTypeCount.totalSeats", 0) +
      _get(flightProduct, "additionalData.paxTypeCount.infant", 0),
    passengers_adult: _get(
      flightProduct,
      "additionalData.paxTypeCount.adult",
      0,
    ),
    passengers_child: _get(
      flightProduct,
      "additionalData.paxTypeCount.child",
      0,
    ),
    passengers_infant_in_seat: 0,
    passengers_infant_in_lap: _get(
      flightProduct,
      "additionalData.paxTypeCount.infant",
      0,
    ),
    items: legItems,
  });
}

export const pushAmadeusSearchResultsEvent = async ({
  language,
  currency,
  user,
}: {
  language: string;
  currency: string;
  user: any;
}) => {
  const provider = _get(user, "provider", "");
  const email = _get(user, "email", "");
  const hashedEmail =
    !!email && provider !== APPLE_PROVIDER ? await sha256(email) : undefined;

  const baseData = {
    _setAccount: "SEE",
    _setDataSource: "dp",
    _setProduct: "5",
    _setLevel: "3",
    _setLanguage: language,
    _setCurrency: currency,
    event: "amd_flights_srp",
  };

  pushDataLayer({
    ...baseData,
    ...(!!hashedEmail && { _setEmail: hashedEmail }),
  });
};

export function pushPaxLoginClickEvent() {
  return pushDataLayerWithParams<MyAccountLoginCtaClickedEventParams>({
    event_name: "myAccount_login_cta_clicked",
    event: AVO_EVENTS.FLIGHT_PAX,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_TRAVELER_DETAILS,
    page: "travelers",
    product_type: "flight",
  });
}

export function pushAdAvailableEvent(adBanner: AdPlacement) {
  return pushDataLayerWithParams<FlightsSrpAdAvailableEventParams>({
    event_name: AVO_EVENT_NAMES.FLIGHTS_SRP_AD_AVAILABLE,
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    ad_type: adBanner.type,
    campaign_name: adBanner.campaignName,
    zone_id: adBanner.zoneId,
    filters_array: adBanner.filters,
  });
}

export function pushAdDisplayedEvent(adBanner: AdPlacement) {
  return pushDataLayerWithParams<FlightsSrpAdDisplayedEventParams>({
    event_name: AVO_EVENT_NAMES.FLIGHTS_SRP_AD_DISPLAYED,
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    ad_type: adBanner.type,
    campaign_name: adBanner.campaignName,
    zone_id: adBanner.zoneId,
    filters_array: adBanner.filters,
  });
}

export function pushAdClickedEvent(adBanner: AdPlacement) {
  return pushDataLayerWithParams<FlightsSrpAdClickedEventParams>({
    event_name: AVO_EVENT_NAMES.FLIGHTS_SRP_AD_CLICKED,
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    ad_type: adBanner.type,
    campaign_name: adBanner.campaignName,
    zone_id: adBanner.zoneId,
  });
}

export function pushFlightAddonClicked({
  isDomestic,
  $resources,
  page,
  itinerary,
  baggageInfo,
  language,
  saleDisplaytotal = null,
  availableAddOns = null,
  rules = null,
  flightProduct = null,
  $currency = null,
}: {
  itinerary: ItineraryModel;
  isDomestic: boolean;
  language: Language;
  $resources: FlightInfoStore["$resources"];
  page: string;
  baggageInfo: LegBaggageInfo[];
  saleDisplaytotal?: any;
  availableAddOns?: AddOnsModel[];
  rules?: RuleProduct[];
  flightProduct?: FlightProductModel;
  $currency?: CurrencyStore;
}) {
  return pushFlightSummaryClickedEvent({
    $resources,
    baggageInfo,
    isDomestic,
    itinerary,
    language,
    page,
    paymentFlightDetailsClickedEventData: {
      event: AVO_EVENTS.FLIGHT_EXTRAS,
      event_category: AVO_EVENT_CATEGORIES.FLIGHTS_ADDONS,
      event_name: AVO_EVENT_NAMES.FLIGHTS_ADDON_FLIGHT_DETAILS_CLICKED,
    },
    saleDisplaytotal,
    $currency,
    availableAddOns,
    flightProduct,
    rules,
  });
}

export function pushFlightSummaryClickedEvent({
  isDomestic,
  $resources,
  page,
  itinerary,
  baggageInfo,
  paymentFlightDetailsClickedEventData = {},
  saleDisplaytotal = null,
  $currency = null,
  availableAddOns = null,
  rules = null,
  flightProduct = null,
}: {
  itinerary: ItineraryModel;
  isDomestic: boolean;
  language: Language;
  $resources: FlightInfoStore["$resources"];
  page: string;
  baggageInfo: LegBaggageInfo[];
  paymentFlightDetailsClickedEventData?: Partial<
    PaymentPdpPageLoadedEventParams & Record<string, any>
  >;
  saleDisplaytotal?: any;
  $currency?: CurrencyStore;
  availableAddOns?: AddOnsModel[];
  rules?: RuleProduct[];
  flightProduct?: FlightProductModel;
}) {
  const {
    displayPrice,
    legs,
    isMultiPNR,
    uniqueCarriersArr,
    // @ts-ignore
    displayCombinedPrice,
    direction,
    searchRequest,
  } = itinerary;
  const { pax, isOneWay, isRoundTrip, preferredCabin, isMultiCity, totalPax } =
    searchRequest;
  const {
    stops,
    departureTime,
    arrivalTime,
    departure,
    arrival,
    originId,
    destinationId,
  } = legs[0] || ({} as LegModel);
  const { departureTime: returnTime, departure: returnDate } = !isOneWay
    ? legs[legs.length - 1]
    : ({} as LegModel);
  const priceProps = displayCombinedPrice || displayPrice;
  const { total, roundtripTotal, cheapestRoundtripTotal, fee } =
    priceProps || ({} as ItineraryPrice<string>);
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");
  const flightCode = _join(_uniq(_map(legs, (leg) => leg.flightCode)), ",");
  const airlineName = uniqueCarriersArr?.join(",");
  const tripType = isRoundTrip ? "roundtrip" : "oneway";
  const price =
    isRoundTrip && direction === "outbound"
      ? cheapestRoundtripTotal?.replaceAll(",", "")
      : roundtripTotal;

  let event = AVO_EVENTS.FLIGHT_PAX;
  let eventName = AVO_EVENT_NAMES.TRAVELER_DETAILS_PAGE_CLICKED;
  const currentPage = page;
  const addonNames = _map(availableAddOns, (addon) => addon.name).join("|");
  const seatsAvailable = _get(itinerary, "seatsAvailable");

  if (currentPage === APP_PAGES.FLIGHTS_EXTRAS) {
    event = AVO_EVENTS.FLIGHT_EXTRAS;
    eventName = AVO_EVENT_NAMES.FLIGHTS_ADDON_FLIGHT_DETAILS_CLICKED;
  }

  return pushDataLayerWithParams<
    Partial<PaymentPdpPageLoadedEventParams & Record<string, any>>
  >({
    event,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_DETAILS,
    event_name: eventName,
    origin_iata: originId,
    destination_iata: destinationId,
    origin_cityName_en: $resources.getAirportCity(originId, LANGUAGE.EN),
    origin_countryName_en: $resources.getAirportCountry(originId, LANGUAGE.EN),
    destination_cityName_en: $resources.getAirportCity(
      destinationId,
      LANGUAGE.EN,
    ),
    destination_countryName_en: $resources.getAirportCountry(
      destinationId,
      LANGUAGE.EN,
    ),
    origin_airportName_en: $resources.getAirportLabel(originId, LANGUAGE.EN),
    destination_airportName_en: $resources.getAirportLabel(
      destinationId,
      LANGUAGE.EN,
    ),
    cabin_class: preferredCabin,
    flight_type: isDomestic ? "dom" : "int",
    trip_type: isMultiCity ? "multicity" : tripType,
    departure_date: departure,
    adult_pax: pax?.adult,
    total_pax: totalPax,
    infant_pax: pax?.infant,
    child_pax: pax?.child,
    departure_datetime_local: format(
      new Date(departureTime),
      DATE_WITH_TIME_FORMAT,
    ),
    arrival_datetime_local: format(
      new Date(arrivalTime),
      DATE_WITH_TIME_FORMAT,
    ),
    airline_iata: airlineName,
    airline_name_en: airlineNameEn,
    flight_number: flightCode,
    price:
      parseFloat(String(isOneWay ? total : price).replaceAll(",", "")) ||
      saleDisplaytotal?.total,
    price_sar: $currency?.convertPriceToCurrency(
      Number(saleDisplaytotal?.total),
      SAR,
    ),
    direct_flight: !stops.length,
    arrival_date: arrival,
    departure_time: format(new Date(departureTime), TIME_FORMAT_24),
    checkin_baggage: !!baggageInfo[0]?.freeBaggage?.checkIn?.isIncluded,
    extra_baggage: false,
    is_multi_pnr: isMultiPNR,
    ...(isRoundTrip && !!returnDate && { return_date: returnDate }),
    ...(returnTime && {
      return_time: format(new Date(returnTime), TIME_FORMAT_24),
    }),
    ...paymentFlightDetailsClickedEventData,
    ...(availableAddOns && { available_addons: addonNames }),
    ...(rules && { service_fee: saleDisplaytotal?.markup || fee }),
    ...(rules && {
      service_fee_code: _join(
        _map(
          _filter(rules, (rule) => rule?.isServiceFee),
          (rule) => rule.code,
        ),
        "|",
      ),
    }),
    ...(flightProduct && {
      frequent_flyer_available: _some(
        flightProduct?.travellers,
        (traveller: TravellerModel) => traveller.hasFrequentFlyerOptions,
      ),
    }),
    seats_remaining:
      Number(seatsAvailable) > 0 ? String(seatsAvailable) : "unavailable",
  });
}

export function pushFareFamilySelectedEvent({
  itinerary,
  $resources,
  fareFamilyUpgraded,
}: {
  itinerary: ItineraryModel;
  $resources: FlightInfoStore["$resources"];
  fareFamilyUpgraded: boolean;
}) {
  const {
    price: itineraryPrice,
    id,
    uniqueCarriersArr,
    searchRequest,
    direction,
    isSingleCabinTrip,
    cabinClassesIncluded,
  } = itinerary || {};
  const isOutbound = direction === OUTBOUND || searchRequest?.isOneWay;
  const eventName = isOutbound
    ? AVO_EVENT_NAMES.FLIGHTS_SRP_OUTBOUND_FAREFAMILY_SELECTED
    : AVO_EVENT_NAMES.FLIGHTS_SRP_INBOUND_FAREFAMILY_SELECTED;
  const {
    originId,
    destinationId,
    preferredCabin,
    searchType,
    pax,
    totalPax,
    isDomesticKSA,
  } = searchRequest || {};
  const {
    cheapestRoundtripTotal,
    roundtripTotal,
    total: totalPrice,
  } = itineraryPrice || {};
  const airlineNameEn = _map(uniqueCarriersArr, (code) =>
    $resources.getAirlineLabel(code, LANGUAGE.EN),
  ).join(",");
  const tripCabin = cabinClassesIncluded[0];
  const cabinClass = isSingleCabinTrip ? tripCabin : preferredCabin;

  return pushDataLayerWithParams<
    FlightsSrpOutboundFareFamilySelectedEventParams | any
  >({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: eventName,
    search_id: id,
    price: roundtripTotal || cheapestRoundtripTotal || totalPrice,
    airline_name_en: airlineNameEn,
    airline_iata: uniqueCarriersArr.join(","),
    fare_family_upgraded: fareFamilyUpgraded,
    origin_iata: originId,
    destination_iata: destinationId,
    cabin_class: cabinClass,
    flight_type: isDomesticKSA ? "dom" : "int",
    trip_type: searchType,
    adult_pax: pax.adult,
    total_pax: totalPax,
    infant_pax: pax.infant,
    child_pax: pax.child,
  });
}

export function pushEasyApplyFilter(action: string, filtersApplied: string[]) {
  return pushDataLayerWithParams<FlightsSrpEasyFiltersEventParams>({
    event_name: AVO_EVENT_NAMES.FLIGHTS_SRP_EASY_APPLY_FILTER,
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    filter_name: filtersApplied.join(","),
    filter_action: action,
  });
}

export function pushQuickFilterEvent(name: string, value: string) {
  return pushDataLayerWithParams<FlightsSrpQuickFiltersEventParams>({
    event: AVO_EVENTS.FLIGHT_SEARCH_RESULTS,
    event_category: AVO_EVENT_CATEGORIES.FLIGHTS_SEARCH_RESULTS,
    event_name: AVO_EVENT_NAMES.FLIGHTS_SRP_QUICK_FILTERS,
    filter_name: name,
    filter_values: value,
  });
}
